import { HttpRequest } from "../utils"

const httpRequest = new HttpRequest()

export interface IDeliveryException {
  id: string
  deliveryNoteId: string
  businessId: string
  exceptionType: string
  attributionDraft: string | null
  attribution: string | null
  attributionConfirmedBy: string | null
  attributionConfirmedAt: string | null
  description: string | null
  notes: string | null
  photoUrls: string[]
  latitude: string | null
  longitude: string | null
  raisedAt: string
  raisedByUserId: string
  attemptNumber: number
  status: string
  resolvedAt: string | null
  resolvedByUserId: string | null
  resolutionNotes: string | null
}

export interface IDeliveryReassignment {
  id: string
  deliveryNoteId: string
  reassignedAt: string
  reassignedByUserId: string
  fromDriverId: string | null
  toDriverId: string | null
  reason: string | null
  reattemptNumber: number
  previousExceptionId: string | null
}

export interface IReportFailedInput {
  exceptionType: string
  attributionDraft?: string | null
  description?: string | null
  notes?: string | null
  photoUrls?: string[]
  latitude?: number | null
  longitude?: number | null
  /** BE failed-report.ts: POSTPONE reschedules the DN, FAILED (default) records a failed attempt. */
  outcome?: "POSTPONE" | "FAILED"
  rescheduledAt?: string | null
}

export interface IReassignInput {
  toDriverId?: string | null
  toVehicleId?: string | null
  toScheduledDate?: string | null
  fromDriverId?: string | null
  fromVehicleId?: string | null
  fromScheduledDate?: string | null
  reason: string
  previousExceptionId?: string | null
}

export const reportFailed = async (
  deliveryNoteId: string,
  body: IReportFailedInput,
): Promise<IDeliveryException> => {
  return httpRequest.post(`/ord/delivery-note/${deliveryNoteId}/failed`, body as any)
}

export interface IDeliveryNoteSummary {
  id: string
  refCode: string | null
  receiverBusiness: { refCode: string | null; profileName: string | null } | null
}

export interface IExceptionsByDnResponse {
  records: IDeliveryException[]
  deliveryNote: IDeliveryNoteSummary | null
}

export const listByDn = async (deliveryNoteId: string): Promise<IExceptionsByDnResponse> => {
  return httpRequest.get(`/ord/delivery-note/${deliveryNoteId}/exceptions`)
}

export const confirmAttribution = async (
  deliveryNoteId: string,
  exceptionId: string,
  body: { attribution: string; resolutionNotes?: string | null },
): Promise<IDeliveryException> => {
  return httpRequest.post(
    `/ord/delivery-note/${deliveryNoteId}/exceptions/${exceptionId}/confirm-attribution`,
    body as any,
  )
}

export const reassign = async (
  deliveryNoteId: string,
  body: IReassignInput,
): Promise<IDeliveryReassignment> => {
  return httpRequest.post(`/ord/delivery-note/${deliveryNoteId}/reassign`, body as any)
}

// Spec §2.5 Tab 6 — global failed-delivery queue (across all DNs).
export interface IDeliveryExceptionWithDn extends IDeliveryException {
  deliveryNote?: {
    id: string
    refCode: string | null
    receiverBusiness: { refCode: string | null; profileName: string | null } | null
  } | null
}

export const list = async (params: {
  status?: string | null
  attribution?: string | null
  exceptionType?: string | null
  from?: string | null
  to?: string | null
  limit?: number
  skip?: number
} = {}): Promise<{ rows: IDeliveryExceptionWithDn[]; count: number }> => {
  const filter: Record<string, string> = {}
  if (params.status) filter.status = params.status
  if (params.attribution) filter.attribution = params.attribution
  if (params.exceptionType) filter.exceptionType = params.exceptionType
  if (params.from) filter.from = params.from
  if (params.to) filter.to = params.to
  const offset: Record<string, number> = {}
  if (params.limit != null) offset.limit = params.limit
  if (params.skip != null) offset.skip = params.skip
  return httpRequest.get(`/ord/delivery-note/failed/list`, { filter, offset })
}
